import { Fragment, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import { updateInhabitantsTableConfig } from '../../api'
import { INHABITANT_ENTITY } from '../../constants/entity'
import { INHABITANT_STATUS } from '../../constants/inhabitant'
import { MENU_INHABITANT_ITEM } from '../../constants/menu'
import { ROUTE_UI_SECTION_NAME, SUBMENU_PARAM_KEY } from '../../constants/route'
import {
  useDefaultParams,
  useInitData,
  useIsUserProfileNGAvailable,
  useModals,
  useNotification,
  useSectionPermissions,
} from '../../hooks'
import { isSubArchive } from '../../utils/route'
import Button from '../Button'
import { withDraftProviders } from '../Draft'
import { EmailDraftTableProvider } from '../Email'
import { FilterBar } from '../Filter'
import {
  getInhabitantCreationFormErrorNotification,
  getInhabitantCreationFormSuccessNotifications,
  getInhabitantTableItemActions,
  getInhabitantTableMassActions,
  INHABITANT_TABLE_MODALS_CONFIG,
  InhabitantFormModal,
  InhabitantTableLayout,
  useInhabitantTableActions,
  useInhabitantTablePage,
} from '../Inhabitant'
import { Page, PageActions, PageLoader } from '../Page'
import { PageActionsButtonAddEntity } from '../PageActionsButtonAddEntity'
import { getEmptyStateActions, PageEmptyState } from '../PageEmptyState'
import { PageHeader } from '../PageHeader'
import { TableExportData } from '../Table'
import { TablePagination } from '../TablePagination'

import {
  CREATE_INHABITANT_ACTION_NAME,
  INHABITANTS_EMAIL_DRAFT_PREFIX,
  QA_NAME_INHABITANTS_TABLE,
  QA_NAME_INHABITANTS_TABLE_ADD_NEW,
  QA_NAME_INHABITANTS_TABLE_STATISTIC_BTN,
} from './Inhabitants.constants'
import { useInhabitantsHandleExport } from './Inhabitants.hooks'
import { InhabitantsStatisticModal } from './InhabitantsStatisticModal'

import * as styles from './Inhabitants.module.css'

import StatisticIcon from '../../icons/statistic.svg'
import UserIcon from '../../icons/user.svg'

function Inhabitants() {
  useDefaultParams()

  const { t } = useTranslation()

  const [searchParams] = useSearchParams()

  const { availableSections } = useInitData()
  const isUserProfileNGAvailable = useIsUserProfileNGAvailable()

  const {
    closeEmailFormModal,
    closeModal,
    modals,
    setEmailFormModal,
    setModal,
  } = useModals(INHABITANT_TABLE_MODALS_CONFIG)

  const sub = searchParams.get(SUBMENU_PARAM_KEY)

  const tablePage = useInhabitantTablePage({
    pageTitle: t('BrowserTab.InhabitantsPageTitleKey'),
    sub,
    onApplyTableConfig: updateInhabitantsTableConfig,
  })

  const {
    availableFilters,
    columns,
    excludeIds,
    filterValues,
    handleChangeShouldReloadFilters,
    handleCleanSelection,
    handleLoadInit,
    handleReload,
    handleReloadTableAfterMassAction,
    handleSetFilterValues,
    handleSetPagination,
    handleUnselectAllPages,
    hasFilterChanged,
    isLoading,
    isRefetching,
    items: inhabitants,
    ordering,
    pageScrollWrapperRef,
    pagination,
    permissions,
    requiredColumns,
    selectAllPages,
    selectedIds,
    serverError,
    shouldReloadFilters,
  } = tablePage

  useEffect(() => {
    if (sub) {
      handleCleanSelection()
      handleLoadInit()
    }
  }, [sub])

  const { canCreate } = useSectionPermissions({
    sectionName: ROUTE_UI_SECTION_NAME.inhabitants,
  })

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const massActions = getInhabitantTableMassActions({ permissions, sub, t })

  function handleOpenStatistic() {
    setModal(<InhabitantsStatisticModal onClose={closeModal} />)
  }

  function handleErrorCreation() {
    handleAddErrorNotification(getInhabitantCreationFormErrorNotification())
  }

  function handleSuccessCreation({ data, isFlatCreated }) {
    closeModal()
    handleReload()

    const notifications = getInhabitantCreationFormSuccessNotifications({
      availableSections,
      data,
      isFlatCreated,
      isUserProfileNGAvailable,
    })

    notifications.forEach(handleAddSuccessNotification)
  }

  function handleOpenCreation() {
    setModal(
      <InhabitantFormModal
        onClose={closeModal}
        onError={handleErrorCreation}
        onSuccess={handleSuccessCreation}
      />
    )
  }

  const {
    handleChangeAddress,
    isPerformingMassAction,
    setItemAction,
    setMassAction,
  } = useInhabitantTableActions({
    excludeIds,
    filterValues,
    pagination,
    selectAllPages,
    selectedIds,
    sub,
    unselectAllPages: handleUnselectAllPages,
    onCleanSelection: handleCleanSelection,
    onCloseEmailFormModal: closeEmailFormModal,
    onCloseModal: closeModal,
    onReload: handleReloadTableAfterMassAction,
    onSetEmailFormModal: setEmailFormModal,
    onSetModal: setModal,
  })

  const { handleOpenExportModal } = useInhabitantsHandleExport({
    excludeIds,
    filterValues,
    selectAllPages,
    selectedIds,
    sub,
    unselectAllPages: handleUnselectAllPages,
    onCloseModal: closeModal,
    onSetModal: setModal,
  })

  const isArchive = isSubArchive(sub)

  const shouldShowEmptyStateActions =
    canCreate && !isArchive && !hasFilterChanged

  const emptyStateActions = getEmptyStateActions({
    actionName: CREATE_INHABITANT_ACTION_NAME,
    isLoading,
    qaName: QA_NAME_INHABITANTS_TABLE_ADD_NEW,
    shouldShowActions: shouldShowEmptyStateActions,
    text: t('InhabitantTable.AddNewBtnKey'),
    onClick: handleOpenCreation,
  })

  const hasInhabitants = inhabitants?.items?.length > 0

  const hideFiltersBar = !hasInhabitants && !hasFilterChanged && !isLoading

  if (!columns || !requiredColumns) {
    return null
  }

  return (
    <Page
      pagination={
        <TablePagination
          responsive
          isSkeleton={isLoading}
          value={pagination}
          onChange={handleSetPagination}
        />
      }
      qaName={QA_NAME_INHABITANTS_TABLE}
      scrollWrapperRef={pageScrollWrapperRef}
      serverError={serverError}
    >
      {isPerformingMassAction || isRefetching ? <PageLoader /> : null}
      <PageHeader
        isLoading={isLoading}
        itemsCount={pagination.count}
        title={t(MENU_INHABITANT_ITEM.title)}
      >
        <PageActions className={styles.pageActions}>
          {permissions.can_export || isLoading ? (
            <TableExportData
              buttonProps={{
                className: styles.button,
                textClassName: styles.buttonText,
              }}
              disabled={!selectAllPages && selectedIds.length === 0}
              isSkeleton={isLoading}
              title='InhabitantTable.ExportBtnKey'
              onClick={handleOpenExportModal}
            />
          ) : null}
          {!hideFiltersBar ? (
            <>
              {permissions.can_view_statistic || isLoading ? (
                <Button.White
                  beforeIconProps={{ Icon: StatisticIcon }}
                  className={styles.button}
                  isSkeleton={isLoading}
                  qaName={QA_NAME_INHABITANTS_TABLE_STATISTIC_BTN}
                  text={t('InhabitantTable.StatisticBtnKey')}
                  textClassName={styles.buttonText}
                  onClick={handleOpenStatistic}
                />
              ) : null}
              {(!isArchive && canCreate) || isLoading ? (
                <PageActionsButtonAddEntity
                  isSkeleton={isLoading}
                  qaName={QA_NAME_INHABITANTS_TABLE_ADD_NEW}
                  text={t('InhabitantTable.AddNewBtnKey')}
                  onClick={handleOpenCreation}
                />
              ) : null}
            </>
          ) : null}
        </PageActions>
      </PageHeader>
      {availableFilters.length > 0 || isLoading ? (
        <FilterBar
          responsive
          availableFilters={availableFilters}
          canViewFilterTemplate={permissions.can_view_filter_template}
          entity={INHABITANT_ENTITY}
          filterValues={filterValues}
          isHidden={hideFiltersBar}
          isSkeleton={isLoading}
          ordering={ordering}
          pagination={{ per_page: pagination.pageSize }}
          setFilterValues={handleSetFilterValues}
          shouldReloadValues={shouldReloadFilters}
          sub={sub}
          onChangeShouldReloadValues={handleChangeShouldReloadFilters}
          onReload={handleReload}
        />
      ) : null}
      <InhabitantTableLayout
        emptyState={
          <PageEmptyState
            EntityIcon={UserIcon}
            actions={emptyStateActions}
            text={t('InhabitantTable.NoDataKey')}
          />
        }
        getInhabitantItemActions={getInhabitantTableItemActions}
        hasTable={hasInhabitants || isLoading}
        massActions={massActions}
        rowStatuses={INHABITANT_STATUS}
        setItemAction={setItemAction}
        setMassAction={setMassAction}
        tablePage={tablePage}
        onChangeAddress={handleChangeAddress}
      />
      {modals.map(([id, element]) => (
        <Fragment key={id}>{element}</Fragment>
      ))}
    </Page>
  )
}

export default withDraftProviders(Inhabitants, [
  { Provider: EmailDraftTableProvider, prefix: INHABITANTS_EMAIL_DRAFT_PREFIX },
])
